import React, { useEffect, useState } from 'react';
import { useNavigate }                from 'react-router-dom';
import { listSchedules }              from '../../api/schedules';
import { authStore }                  from '../../api/client';

/**
 * UpcomingAgenda - dashboard widget listing the host's next 7 days of
 * scheduled interviews. Phase 9.
 *
 * Each row offers a "Start Session" button when the scheduled time is
 * within +/- 15 minutes of "now".
 */
const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const SEVEN_DAYS_MS  = 7  * 24 * 60 * 60 * 1000;

function fmt(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric',
                                         hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export default function UpcomingAgenda() {
  const navigate = useNavigate();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    const role = authStore.role;
    const email = authStore.user?.email;
    const params =
      role === 'SUPER_ADMIN'
        ? {}
        : email
          ? { hostEmail: email }
          : null;
    if (params === null) {
      setLoading(false);
      setError('Sign in to see your schedule.');
      return () => { cancelled = true; };
    }
    console.log('[UpcomingAgenda] loading schedules', params);
    listSchedules(params)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        const all = Array.isArray(raw)
          ? raw
          : (raw?.content ?? raw?.schedules ?? []);
        const cutoff = Date.now() + SEVEN_DAYS_MS;
        const upcoming = all
          .filter((s) => {
            const t = s.scheduledAt ? new Date(s.scheduledAt).getTime() : 0;
            return t >= Date.now() - FIFTEEN_MIN_MS && t <= cutoff && s.status !== 'CANCELLED';
          })
          .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
          .slice(0, 8);
        setItems(upcoming);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[UpcomingAgenda] load failed:', err.message);
        setError(err.message || 'Failed to load schedules.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function isStartable(iso) {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Math.abs(t - Date.now()) <= FIFTEEN_MIN_MS;
  }

  if (loading) {
    return <p className="text-xs text-slate-400 text-center py-6">Loading agenda...</p>;
  }
  if (error) {
    return <p className="text-xs text-red-500 text-center py-6">{error}</p>;
  }
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 text-center py-6">Nothing scheduled in the next 7 days.</p>;
  }

  return (
    <ul className="divide-y divide-line-soft">
      {items.map((s) => {
        const startable = isStartable(s.scheduledAt);
        return (
          <li key={s.id} className="py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink truncate">
                {s.candidateName || s.candidateEmail || 'Candidate'}
              </p>
              <p className="text-xs text-ink-faint">{fmt(s.scheduledAt)}</p>
            </div>
            {startable ? (
              <button
                onClick={() => navigate(s.sessionId ? `/session/${s.sessionId}` : '/schedules')}
                className="btn btn-sm btn-primary text-xs"
              >
                Start Session
              </button>
            ) : (
              <span className="text-[11px] font-mono text-ink-faint uppercase tracking-wider">
                {s.status || 'SCHEDULED'}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
